import { createReference, getNameText } from '@quromedical/fhir-common';
import { PartialPractitionerWithId } from '@quromedical/models';
import { sortby } from '@quromedical/utils';
import { SelectOption } from 'components/types';
import { useMemo } from 'react';
import { strings } from 'strings';
import { trpc } from 'trpc';

type UsePractitionersByOrgFetcher = {
  mapPractitionerOptions: (
    practitioners: PartialPractitionerWithId[]
  ) => SelectOption<string, string>[];
  options: SelectOption<string, string>[];
  isLoading: boolean;
};

type UsePractitionersByOrg = {
  practitioners: PartialPractitionerWithId[];
  isLoading: boolean;
};

const mapPractitionerOptions = (practitioners: PartialPractitionerWithId[]) => {
  const orderedPractitioners = sortby(
    practitioners,
    practitioner => practitioner.general.givenName
  );

  const practitionerOptions = orderedPractitioners.map<SelectOption<string, string>>(
    practitioner => ({
      value: createReference('Practitioner', practitioner.id).reference,
      display:
        getNameText(practitioner.general.givenName, practitioner.general.familyName) ||
        strings.DisplayNotFound,
    })
  );

  return practitionerOptions;
};

export const usePractitionerByOrgFetcher = (orgId: string[]): UsePractitionersByOrgFetcher => {
  const { practitioners, isLoading } = usePractitionerByOrg(orgId);

  const options = useMemo(() => mapPractitionerOptions(practitioners), [practitioners]);

  return { options, isLoading, mapPractitionerOptions };
};

export const usePractitionerByOrg = (orgId: string[]): UsePractitionersByOrg => {
  const ids = orgId.join(',');

  const {
    data,
    isRefetching,
    isLoading: isLoadingData,
  } = trpc.context.practitioner.getByOrganizations.useQuery(
    { ids },
    {
      enabled: !!ids.length,
      refetchOnWindowFocus: false,
    }
  );

  const practitioners = useMemo(() => data?.practitioners || [], [data]);

  const isLoading = useMemo(() => {
    return isLoadingData || isRefetching;
  }, [isLoadingData, isRefetching]);

  return { practitioners, isLoading };
};
